import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Subscription, catchError, of } from 'rxjs';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import {
  FilterDropdownComponent,
  FilterDropdownOption,
} from '../../components/filter-dropdown/filter-dropdown.component';
import { OverviewPaginationComponent } from '../../components/overview-pagination/overview-pagination.component';
import { StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { DefectService } from '../../services/defect.service';
import { ReleaseService } from '../../services/release.service';
import { ProjectMemberService } from '../../services/project-member.service';
import { DefectListFilters, DefectListItem, DefectStatus } from '../../models/defect.model';
import { Release } from '../../models/release.model';
import { ProjectMemberDTO } from '../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../shared/project-member-options';
import { DEFECT_STATUSES, defectStatusKind } from './defect-status';

const PAGE_SIZE_OPTIONS: readonly number[] = [10, 20, 50];

/** Design-Luecke bis NEX-1690: Aufbau folgt der Objektbibliotheken-Liste (Kopfzeile, Filter, Kartentabelle). */
@Component({
  selector: 'app-defects-page',
  imports: [
    TranslatePipe,
    MatButton,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    FilterDropdownComponent,
    OverviewPaginationComponent,
    StatusPillComponent,
  ],
  templateUrl: './defects-page.component.html',
  styleUrl: './defects-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefectsPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly defectService = inject(DefectService);
  private readonly releaseService = inject(ReleaseService);
  private readonly memberService = inject(ProjectMemberService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private request?: Subscription;

  readonly projectId: string = this.route.snapshot.params['projectId'];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly statusKind = defectStatusKind;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly defects = signal<DefectListItem[]>([]);
  readonly totalElements = signal(0);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(20);

  readonly statusFilter = signal('');
  readonly releaseFilter = signal('');
  readonly assigneeFilter = signal('');

  private readonly releases = signal<Release[]>([]);
  private readonly members = signal<ProjectMemberDTO[]>([]);

  readonly hasFilter = computed(() => !!(this.statusFilter() || this.releaseFilter() || this.assigneeFilter()));

  readonly statusOptions = computed<FilterDropdownOption[]>(() =>
    DEFECT_STATUSES.map((status) => ({ value: status, label: this.translate.instant(`defect.status.${status}`) })),
  );

  readonly releaseOptions = computed<FilterDropdownOption[]>(() =>
    this.releases().map((release) => ({ value: release.id, label: release.label })),
  );

  readonly assigneeOptions = computed<FilterDropdownOption[]>(() =>
    reviewerOptionsFromMembers(this.members()).map((person) => ({ value: person.id, label: person.displayName })),
  );

  ngOnInit(): void {
    this.releaseService
      .list(this.projectId)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((releases) => this.releases.set(releases));
    this.memberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => this.members.set(members));
    this.load();
  }

  load(): void {
    this.request?.unsubscribe();
    this.loading.set(true);
    this.loadError.set(false);
    this.request = this.defectService
      .list(this.projectId, this.filters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          this.defects.set(page.content);
          this.totalElements.set(page.totalElements);
          this.loading.set(false);
        },
        error: () => {
          this.defects.set([]);
          this.totalElements.set(0);
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  onStatusFilter(value: string): void {
    this.statusFilter.set(value);
    this.reloadFromStart();
  }

  onReleaseFilter(value: string): void {
    this.releaseFilter.set(value);
    this.reloadFromStart();
  }

  onAssigneeFilter(value: string): void {
    this.assigneeFilter.set(value);
    this.reloadFromStart();
  }

  onPageIndex(index: number): void {
    this.pageIndex.set(index);
    this.load();
  }

  onPageSize(size: number): void {
    this.pageSize.set(size);
    this.reloadFromStart();
  }

  open(defect: DefectListItem): void {
    this.router.navigate(['/projects', this.projectId, 'defects', defect.id]);
  }

  private reloadFromStart(): void {
    this.pageIndex.set(0);
    this.load();
  }

  private filters(): DefectListFilters {
    const filters: DefectListFilters = { page: this.pageIndex(), size: this.pageSize() };
    if (this.statusFilter()) filters.status = [this.statusFilter() as DefectStatus];
    if (this.releaseFilter()) filters.releaseId = this.releaseFilter();
    if (this.assigneeFilter()) filters.assigneeUserId = this.assigneeFilter();
    return filters;
  }
}
